import { TRENDING_RAIL_SECTION_ID, TRENDING_RAIL_SECTION_TITLE } from '@/client/confs/app';
import type { Section, SectionPrediction } from '@/libs/sdk/api';

// Same event-vs-market detection MarketChartSection uses: explicit `type`
// wins, otherwise fall back to id shape (market predictions are 0x-prefixed
// addresses, event predictions aren't).
const isEventPrediction = (prediction: SectionPrediction): boolean =>
  prediction.type === 'event' || !prediction.prediction?.startsWith('0x');

// The pinned id is per-tenant: AIP pins a real section uuid, SakerX ships it
// unset and matches on the title alone. The emptiness check is load-bearing --
// without it an unset constant turns into `section.id === ''`, which claims any
// section whose id failed to load as the trending rail.
export const isTrendingRailSection = (section: Pick<Section, 'id' | 'title'>): boolean =>
  (!!TRENDING_RAIL_SECTION_ID && section.id === TRENDING_RAIL_SECTION_ID) ||
  section.title === TRENDING_RAIL_SECTION_TITLE;

// Curated pins for the home "Trending now" rail, highest weight first (same
// sort MarketChartSection uses for its own predictions). `sections` is
// already lens-scoped -- get-sections takes baseTokenAddress and returns only
// that lens's mirror events for a dual-collateral pin, so no separate
// client-side collateral filter is needed here.
export const selectTrendingSectionEventIds = (sections: Section[] | undefined): string[] => {
  const section = sections?.find(isTrendingRailSection);
  if (!section) return [];
  return [...section.predictions]
    .sort((a, b) => b.weight - a.weight)
    .filter(isEventPrediction)
    .map(prediction => prediction.prediction);
};

// Section missing, or present but empty/non-event after filtering -> caller
// falls back to the volume-sorted rail (cold-start lens with nothing pinned).
export const shouldFallbackToVolumeTrending = (eventIds: string[]): boolean => eventIds.length === 0;
